import React, { useState } from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import { Modal } from 'react-bootstrap'
import { breakpoints, colors } from 'globalStyles'
import signatureSVG from 'assets/svg/pen-to-square-black.svg'
import Button from 'react-bootstrap/Button'
import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'
import { setShipmentPreferences } from 'store/shipping/shippingSlice'
import { selectDeliverySummary } from 'store/shipping/shippingSelectors'
import SuccessModal from 'components/SuccessModal'
import SignatureReleaseAuthorization from './SignatureReleaseAuthorization'

const StyledPopUp = styled(Modal)`
  .modal-dialog {
    display: flex !important;
    align-items: center !important;
  }
  .modal-dialog.modal-dialog-centered {
    justify-content: center;
    align-items: center;
  }
  .modal-content {
    position: relative;
    width: 100%;
  }
  @media ${breakpoints.phone}, ${breakpoints.tablet} {
    .modal-content {
      width: 330px !important;
    }
  }
  @media ${breakpoints.desktop} {
    .modal-content {
      width: 500px;
    }
  }
  .modal-header {
    background-color: ${colors.translucent_light_blue};
    padding: 0px;
    min-height: 78px;
    justify-content: center;
    align-items: center;
    display: flex;

    img {
      width: 25px;
      height: 25px;
    }
  }
  .modal-title {
    font-weight: 600;
    font-size: 20px;
    line-height: 26px;
    margin-left: 13.5px;
    text-align: center;
    @media ${breakpoints.phone}, ${breakpoints.tablet} {
      width: 162px;
    }
  }
  .modal-body {
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .modal-footer {
    width: 100% !important;
    border-top: none;
    justify-content: center;
    margin-top: -10px;
    gap: 8px;
    @media ${breakpoints.desktop} {
      gap: 16px;
      margin-bottom: 10px;
    }
  }

  button.popUpSecBtn.btn.btn-secondary,
  button.popUpPrimBtn.btn.btn-primary {
    width: 132px !important;
    @media ${breakpoints.sm}, ${breakpoints.tablet} {
      width: 140px !important;
    }
    @media ${breakpoints.desktop} {
      width: 218px !important;
    }
  }
`

const SignatureReleasePopUp = ({ show, onHide, popUpType }) => {
  const [enableSaveButton, setEnableSaveButton] = useState(false)
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const { deliveryLocation, buzzerCode, additionalDeliveryInstructions } =
    useSelector(selectDeliverySummary)

  const cancelSuccessModal = () => {
    setShowSuccessModal(false)
  }

  const handleClick = (signaturePreference, previousSignaturePref) => {
    dispatch(
      setShipmentPreferences(
        additionalDeliveryInstructions,
        buzzerCode,
        deliveryLocation,
        signaturePreference,
        previousSignaturePref
      )
    )
    onHide()
    const message = t('deliveryPreferencesSummary.signature_update_success')
    setSuccessMessage(message)
    setShowSuccessModal(true)
    setTimeout(() => {
      setShowSuccessModal(false)
    }, 3000)
  }

  const isSaveEnabled = (value) => {
    setEnableSaveButton(value)
  }

  const modalType = {
    signAuth: {
      title: t('set_preferences.waive_sign'),
      icon: signatureSVG,
      saveText: t('buttonActions.save'),
      cancelText: t('buttonActions.cancel'),
      component: (
        <SignatureReleaseAuthorization handleClick={handleClick} isSaveEnabled={isSaveEnabled} />
      ),
      isSaveDisabled: !enableSaveButton,
      onCancel: () => {
        onHide()
      },
    },
  }

  const modalData = modalType[popUpType]
  return (
    <>
      <StyledPopUp show={show} backdrop="static" centered>
        <StyledPopUp.Header>
          {modalData.icon && <img className="icon-padding" src={modalData.icon} alt="icon-img" />}
          <StyledPopUp.Title>{modalData.title}</StyledPopUp.Title>
        </StyledPopUp.Header>
        <StyledPopUp.Body>{modalData.component}</StyledPopUp.Body>
        <StyledPopUp.Footer>
          <Button variant="secondary" onClick={modalData.onCancel} className="popUpSecBtn">
            {modalData.cancelText}
          </Button>
          <Button
            type="submit"
            variant="primary"
            form="signForm"
            disabled={modalData.isSaveDisabled}
            className="popUpPrimBtn"
          >
            {modalData.saveText}
          </Button>
        </StyledPopUp.Footer>
      </StyledPopUp>
      <SuccessModal show={showSuccessModal} onHide={cancelSuccessModal} message={successMessage} />
    </>
  )
}

SignatureReleasePopUp.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  popUpType: PropTypes.string.isRequired,
}

export default SignatureReleasePopUp
